/**
 * The functions in this file are commonly used between the hydrographs
 * and to implement DRY and to less duplicate code they kept in this file
 * and imported from Chart.svelte, base.js and Legend.svetle for both hydrographs.
 *
 * Sources:
 * https://www.timeanddate.com/time/zone/uk/london
 * https://metacpan.org/dist/Date-Manip/view/lib/Date/Manip/Zones.pod
 * https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
 */
export const timeZones = [
  {
    abbr: ['AST10ADT'],
    name: 'America/Adak',
  },
  {
    abbr: ['AST4ADT'],
    name: 'America/Halifax',
  },
  {
    abbr: ['AST4', 'AST4APT'], // No DST
    name: 'America/Puerto_Rico',
  },
  {
    abbr: ['AST9ADT'],
    name: 'America/Anchorage',
  },
  {
    abbr: ['CST6CDT'],
    name: 'America/Chicago',
  },
  {
    abbr: ['EST5CDT', 'EST5EST', 'EST6CDT'], // Began using DST in 2006
    name: 'America/Indiana/Indianapolis',
  },
  {
    abbr: ['EST5EDT'],
    name: 'America/New_York',
  },
  {
    abbr: ['GMT0GMT', 'UTC'], // No DST
    name: 'Etc/UTC',
  },
  {
    abbr: ['GUM-10', 'GST-10GDT', 'CHST'], // No DST
    name: 'Pacific/Guam',
  },
  {
    abbr: ['HST10'], // No DST
    name: 'Pacific/Honolulu',
  },
  {
    abbr: ['MST7MDT'],
    name: 'America/Denver',
  },
  {
    abbr: ['MST7', 'MST7MST'], // No DST
    name: 'America/Phoenix',
  },
  {
    abbr: ['PST8PDT'],
    name: 'America/Los_Angeles',
  },
  {
    abbr: ['SST'],
    name: 'Pacific/Pago_Pago',
  },
  {
    abbr: ['YST9YDT'],
    name: 'America/Yakutat',
  },
]

/**
 * If y0 is hidden, this helps remove excessive dark-grey y-axis background lines
 * once ticks has surpassed this number.
 */
export const EXCESSIVE_TICKS_BG_STYLE_HELP_THRESHOLD = 199

/**
 * Styles based on Nurtur Nature graphic and existing NWM 2.0 charting. TM
 */
export let seriesStyle = {
  visibleDefaults: ['observed', 'forecast', 'analysis_assim', 'short_range'],
  observed: {
    name: 'Observed',
    color: '#2056a7', // Series line color
    sfkey: 'observed',
    lineWidth: 3,
    axis: 0,
    marker: {
      symbol: 'circle',
      //fillColor: ,  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 0, // Outline width
      radius: 3, // Size of the marker
    },
  },
  forecast: {
    name: 'Forecast',
    color: '#662d91', // Series line color
    sfkey: 'forecast',
    lineWidth: 3,
    axis: 0,
    marker: {
      symbol: 'square',
      //fillColor: ,  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 0, // Outline width
      radius: 3, // Size of the marker
    },
  },
  short_range: {
    name: 'Short Range',
    color: '#ff6699', // Series line color
    sfkey: 'shortRange',
    lineWidth: 3,
    axis: 0,
    marker: {
      symbol: 'square',
      //fillColor: ,  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 0, // Outline width
      radius: 3, // Size of the marker
    },
  },
  analysis_assimilation: {
    name: 'Analysis',
    color: '#ff8c66', // Series line color
    sfkey: 'analysisAssim',
    lineWidth: 3,
    axis: 1,
    marker: {
      symbol: 'square',
      //fillColor: ,  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 0, // Outline width
      radius: 3, // Size of the marker
    },
  },
  medium_range: {
    name: 'Medium Range',
    color: '#d2a1e2', // Series line color
    sfkey: 'mediumRange',
    lineWidth: 3,
    axis: 1,
    marker: {
      symbol: 'square',
      fillColor: '#c482ff', // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 0, // Outline width
      radius: 3, // Size of the marker
    },
  },
  medium_range_blend: {
    name: 'Medium Range Blend',
    color: '#d2a1e2', // Series line color
    sfkey: 'mediumRange',
    lineWidth: 3,
    axis: 1,
    marker: {
      symbol: 'square',
      fillColor: '#c482ff', // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 0, // Outline width
      radius: 3, // Size of the marker
    },
  },
  long_range: {
    name: 'Long Range',
    color: '#668cff', // Series line color
    sfkey: 'longRange',
    lineWidth: 3,
    axis: 1,
    marker: {
      symbol: 'square',
      //fillColor: '#c482ff',  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 0, // Outline width
      radius: 3, // Size of the marker
    },
  },
  record_stageline: {
    name: 'Record Stage',
    color: '#89d7f7', // Series line color from Nurture Nature
    sfkey: 'recordStageline',
    lineWidth: 3,
    marker: {
      symbol: 'dash',
      //fillColor: ,  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 2, // Outline width
      radius: 3, // Size of the marker
    },
  },
  normal_pool: {
    name: 'Normal Pool',
    //color: '#89d7f7', // Series line color from Nurture Nature
    color: '#000000', // Series line color from Nurture Nature
    sfkey: 'normalPool',
    lineWidth: 3,
    marker: {
      symbol: 'dash',
      //fillColor: ,  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 2, // Outline width
      radius: 3, // Size of the marker
    },
  },
  lowwater_stageline: {
    name: 'LowWater Stage',
    color: '#906320', // Modified per OWP-795
    sfkey: 'lowwaterStageline',
    lineWidth: 3,
    marker: {
      symbol: 'dotted',
      //fillColor: ,  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 2, // Outline width
      radius: 3, // Size of the marker
    },
  },

  // Forecast Ensemble members
  // https://www.nohrsc.noaa.gov/json/nwm-forecast-chart-config.json
  long_range_ensemble_member1: {
    name: 'Long Range Member 1',
    color: '#8ca9ff', // Series line color
    sfkey: 'longRange',
    lineWidth: 3,
    axis: 1,
    marker: {
      symbol: 'dotted',
      //fillColor: ,  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 2, // Outline width
      radius: 3, // Size of the marker
    },
  },
  long_range_ensemble_member2: {
    name: 'Long Range Member 2',
    color: '#8ca9ff', // Series line color
    sfkey: 'longRange',
    lineWidth: 3,
    axis: 1,
    marker: {
      symbol: 'dotted',
      //fillColor: ,  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 2, // Outline width
      radius: 3, // Size of the marker
    },
  },
  long_range_ensemble_member3: {
    name: 'Long Range Member 3',
    color: '#8ca9ff', // Series line color
    sfkey: 'longRange',
    lineWidth: 3,
    axis: 1,
    marker: {
      symbol: 'dotted',
      //fillColor: ,  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 2, // Outline width
      radius: 3, // Size of the marker
    },
  },
  long_range_ensemble_member4: {
    name: 'Long Range Member 4',
    color: '#8ca9ff', // Series line color
    sfkey: 'longRange',
    lineWidth: 3,
    axis: 1,
    marker: {
      symbol: 'dotted',
      //fillColor: ,  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 2, // Outline width
      radius: 3, // Size of the marker
    },
  },
  long_range_ensemble_mean: {
    name: 'Long Range Ensemble Mean',
    color: '#668cff', // Series line color
    sfkey: 'longRange',
    lineWidth: 3,
    axis: 1,
    unitString: 'cfs',
    marker: {
      symbol: 'dotted',
      //fillColor: ,  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 2, // Outline width
      radius: 3, // Size of the marker
    },
  },

  medium_range_ensemble_member1: {
    name: 'Medium Range Member 1',
    color: '#d2a1e2', // Series line color
    sfkey: 'mediumRange',
    lineWidth: 3,
    axis: 1,
    marker: {
      symbol: 'dotted',
      //fillColor: ,  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 2, // Outline width
      radius: 3, // Size of the marker
    },
  },
  medium_range_ensemble_member2: {
    name: 'Medium Range Member 2',
    color: '#d2a1e2', // Series line color
    sfkey: 'mediumRange',
    lineWidth: 3,
    lineWidth: 3,
    axis: 1,
    marker: {
      symbol: 'dotted',
      //fillColor: ,  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 2, // Outline width
      radius: 3, // Size of the marker
    },
  },
  medium_range_ensemble_member3: {
    name: 'Medium Range Member 3',
    color: '#d2a1e2', // Series line color
    sfkey: 'mediumRange',
    lineWidth: 3,
    lineWidth: 3,
    axis: 1,
    marker: {
      symbol: 'dotted',
      //fillColor: ,  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 2, // Outline width
      radius: 3, // Size of the marker
    },
  },
  medium_range_ensemble_member4: {
    name: 'Medium Range Member 4',
    color: '#d2a1e2', // Series line color
    sfkey: 'mediumRange',
    lineWidth: 3,
    lineWidth: 3,
    axis: 1,
    marker: {
      symbol: 'dotted',
      //fillColor: ,  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 2, // Outline width
      radius: 3, // Size of the marker
    },
  },
  medium_range_ensemble_member5: {
    name: 'Medium Range Member 5',
    color: '#d2a1e2', // Series line color
    sfkey: 'mediumRange',
    lineWidth: 3,
    lineWidth: 3,
    axis: 1,
    marker: {
      symbol: 'dotted',
      //fillColor: ,  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 2, // Outline width
      radius: 3, // Size of the marker
    },
  },
  medium_range_ensemble_member6: {
    name: 'Medium Range Member 6',
    color: '#d2a1e2', // Series line color
    sfkey: 'mediumRange',
    lineWidth: 3,
    lineWidth: 3,
    axis: 1,
    marker: {
      symbol: 'dotted',
      //fillColor: ,  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 2, // Outline width
      radius: 3, // Size of the marker
    },
  },
  medium_range_ensemble_member7: {
    name: 'Medium Range Member 7',
    color: '#d2a1e2', // Series line color
    sfkey: 'mediumRange',
    lineWidth: 3,
    lineWidth: 3,
    axis: 1,
    marker: {
      symbol: 'dotted',
      //fillColor: ,  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 2, // Outline width
      radius: 3, // Size of the marker
    },
  },
  medium_range_ensemble_mean: {
    name: 'Medium Range Ensemble Mean',
    color: '#d966ff', // Series line color
    sfkey: 'mediumRange',
    lineWidth: 3,
    lineWidth: 3,
    unitString: 'cfs',
    axis: 1,
    marker: {
      symbol: 'dotted',
      //fillColor: ,  // Marker fill color
      //lineColor: ,  // Outline color
      lineWidth: 2, // Outline width
      radius: 3, // Size of the marker
    },
  },

  // TODO: Add other series styles. TM
  // https://www.nohrsc.noaa.gov/json/nwm-forecast-chart-config-test.json
  major: {
    color: 'rgba(252, 233, 254, 1)',
  },
  moderate: {
    color: 'rgba(255, 229, 230, 1)',
  },
  flood: {
    color: 'rgba(254, 245, 228, 1)',
  },
  action: {
    color: 'rgba(255,255, 231, 1)',
  },
  record: {
    color: 'rgba(102,178,255,0.1)',
  },
  low: {
    color: 'rgba(235, 233, 201, 1)',
  },
}

/**
 * Parse the NRLDB `location.tzone` field to a proper timezone name (i.e America/Los_Angeles) TM
 * @param array abbv
 * @returns string
 */
export function setSiteTimeZone(abbv) {
  let tzo = timeZones.filter((z) => z.abbr.includes(abbv.toUpperCase()) == true)

  if (tzo.length) {
    return tzo[0].name
  }

  // Default to UTC time.
  // TODO: Should probably do the filter to do this properly rather than hard code... TM
  // TODO: For NWM reaches not attached to gauges, maybe show in users local time? TM
  return 'Etc/UTC'
}

/**
 * Converts from CFS to KCFS value.
 * @param string value
 * @returns Number | value
 */
export function cfsToKcfs(value, prec = 2) {
  return !isMissing(value) ? Number(parseFloat(value / 1000).toFixed(prec)) : value
}

/**
 * Capitalizes first letter of word
 * @param string str
 * @returns string
 */
export function ucFirst(str) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Capitalize the first letter in a string, similar to PHP's ucfirst function
 * @param {*} str
 * @returns
 */
export function ucWords(str) {
  let ret = ''
  str.split(' ').forEach((i) => {
    ret += ucFirst(i) + ' '
  })
  return ret.trim()
}

/**
 * isMissing checks if a stage/flow value is valid. AHPS uses
 * -999/-9999 for missing values.
 * @param string value
 * @returns string
 */
export function isMissing(value) {
  return value === null || value === undefined || value <= -999 ? true : false
}

/**
 * Setup the links in the custom legend
 * @param object chartSeries
 * @param string type
 * @returns object
 */
export function getLegendSections(chartSeries, type) {
  return chartSeries.filter((i) => type.includes(i.userOptions.id))
}

export function imageResize(chart) {
  chart.plotBackground.attr({
    x: chart.chartWidth / 2 - 150,
    y: 95,
    width: 300,
    height: 300,
  })
  if (chart.plotWidth <= 330) {
    chart.plotBackground.attr({
      x: chart.chartWidth / 2 - (chart.plotWidth - 30) / 2,
      y: 95 + (330 - chart.plotWidth) / 2,
      width: chart.plotWidth - 30,
      height: chart.plotWidth - 30,
    })
    return
  }
}

export function addNOAAWaterMark(chart) {
  chart.plotBackground = chart.renderer
    .image(
      '/assets/styles/public/images/noaa-logo.png',
      // x refers to the left most side of the image. To center we get the center of the chart and subtract half the image width
      chart.chartWidth / 2 - 150,
      95,
      300,
      300
    )
    .attr({
      zIndex: 1,
      opacity: 0.15,
    })
    .add()
}

/**
 * Chart plot area watermark seen in red in middle
 * @param object chart
 * @param string text
 * @param Timestamp now
 * @returns true
 */
export function chartWaterMark(chart, text, now = null) {
  // Get pixel coord of the "now" line on the xAxis
  let n = chart.xAxis[0].toPixels(now)

  //If no data - do not place forecast watermark
  if (isNaN(n)) {
    return
  }
  // Size to Work With
  let sww = chart.plotWidth - n
  // Good initial guess on the proper font size
  // let f = Math.round(sww * 0.25)
  let f = Math.round(sww * 0.18)
  let maxFontSize = 55
  // max font size is 55
  f = f > maxFontSize ? maxFontSize : f

  // If we already have a watermark, update the fontsize and X,Y
  if (chart.watermark) {
    chart.watermark.attr({
      x: n + chart.watermark.getBBox().width * 0.05,
      y: chart.plotHeight / 2,
    })

    // TODO: need to fix this to better catch edge cases where it bounces between font sizes and the +-10px buffer isn't enough.
    while (
      f > 0 &&
      f <= maxFontSize &&
      (chart.watermark.getBBox().width > sww + 10 || chart.watermark.getBBox().width < sww - 10)
    ) {
      if (chart.watermark.getBBox().width > sww) {
        f -= 1
      } else {
        f += 1
      }

      chart.watermark
        .css({
          fontSize: `${f}px`,
        })
        .attr({
          x: n + chart.watermark.getBBox().width * 0.05,
          y: chart.plotHeight / 2,
        })
    }

    return true
  }

  // If no watermark, creat one
  chart.watermark = chart.renderer
    .label(text, chart.plotWidth / 2 + chart.plotWidth * 0.05, chart.plotHeight / 2)
    .css({
      color: 'rgb(192, 192, 192, .15)',
      fontSize: `${f}px`,
      stroke: 'rgb(153, 153, 153, .15)',
      textShadow: '1px 1px 1px rgb(0, 0, 0, 0.85)',
    })
    .attr({
      color: 'rgb(249, 162, 162, 0.15)',
      zIndex: 1,
      'font-style': 'italic',
      'font-weight': 'bold',
    })
    .add()

  return true
}

/**
 * If chart has not series data then remove elements
 * to look more polished.
 * @param object chart
 * @param object series
 */
export function chartHasNoData(chart, series) {
  if (
    !chart.hasData() &&
    series &&
    series.nwm &&
    series.nwm.originals &&
    Object.keys(series.nwm.originals).length === 0
  ) {
    if (series.version.chart === 'official') {
      // remove the currentDateTimeLine
      chart.xAxis[0].removePlotLine('currentDateTimeLine')

      // remove latest observed value box
      let obsBox = document.querySelector('#test_official')
      if (obsBox) obsBox.remove()
    } else {
      // remove the currentDateTimeLine
      chart.xAxis[0].removePlotLine('currentDateTimeLineNWM')

      // remove latest observed value box
      let obsBox = document.querySelector('#test_nwm')
      if (obsBox) obsBox.remove()
    }
  }
}

/**
 * Looks up a value on the ratings table and returns computed
 * related value. DO NOT alter this function as it does A LOT!!
 * @param {*} curve  this is the ratingsCurve
 * @param {*} value
 * @param {*} type the value coming in if it is Stage or Flow
 * @param {*} flowUnits 'cfs' or 'kcfs'; only applies to flow data that is coming in
 * @returns the value opposite of the type (Stage or Flow)
 */
export function ratingsLookup(curve, value, type = 'stage', flowUnits = 'cfs', getlimit = false) {
  if (!curve) return false
  type = type.toLowerCase()

  // If checking type 'flow' we make sure the value is CFS: we are comparing flow units as value CFS to curve CFS.
  if (type === 'flow' && flowUnits === 'kcfs') value = kcfsToCfs(value)

  // Check for values outside of the ratings range
  if (type == 'flow' && parseFloat(value).toFixed(2) < parseFloat(curve[0][type])) {
    // We want to return the max flow value (the opposite) or unknown
    return getlimit ? curve[0]['flow'] : -999
  }
  if (type == 'flow' && parseFloat(value).toFixed(2) > parseFloat(curve[curve.length - 1][type])) {
    // We want to return the max flow value (the opposite) or unknown
    return getlimit ? curve[curve.length - 1]['flow'] : -999
  }
  if (type == 'stage' && parseFloat(value).toFixed(2) < parseFloat(curve[0][type])) {
    // We want to return the max stage value (the opposite) or unknown
    return getlimit ? curve[0]['stage'] : -999
  }
  if (type == 'stage' && parseFloat(value).toFixed(2) > parseFloat(curve[curve.length - 1][type])) {
    // We want to return the max stage value (the opposite) or unknown
    return getlimit ? curve[curve.length - 1]['stage'] : -999
  }
  let lowstage, highstage, lowflow, highflow, outValue

  for (let i = 0; i < curve.length; i++) {
    if (type === 'stage') {
      // Return Flow value
      if (value == curve[i].stage) {
        outValue = curve[i].flow
        break
      } else if (value < curve[i].stage) {
        highstage = curve[i].stage
        highflow = curve[i].flow
        lowstage = curve[i > 0 ? i - 1 : 0].stage
        lowflow = curve[i > 0 ? i - 1 : 0].flow
        outValue = parseInt((((value - lowstage) / (highstage - lowstage)) * (highflow - lowflow) + lowflow).toFixed(0))
        break
      }
    } else {
      // Return Stage value
      if (value == curve[i].flow) {
        outValue = curve[i].stage
        break
      } else if (value < curve[i].flow) {
        highstage = curve[i].stage
        highflow = curve[i].flow
        lowstage = curve[i > 0 ? i - 1 : 0].stage
        lowflow = curve[i > 0 ? i - 1 : 0].flow
        outValue = parseFloat(((value - lowflow) / (highflow - lowflow)) * (highstage - lowstage) + lowstage).toFixed(2)
        break
      }
    }
  }
  return Number(outValue)
}

/**
 * kcfsToCfs converts a kcfs value to cfs (cubic feet per second)
 * @param {*} value
 * @returns
 */
export function kcfsToCfs(value, prec = 2) {
  return Number(parseFloat(value * 1000).toFixed(prec))
}

/**
 * Get the gauge configuration for the flow units settings.
 * With this function just pass the "gaugeConfig && gaugeConfig.hydrograph" or
 * the "gaugeConfig && gaugeConfig.hydrographNWM" depending on the component.
 * @param {*} hgType 'hydrograph' or 'hydrographNWM'
 * @returns
 */
export function getFlowUnitsByConfig(hgType) {
  let p = hgType.flowUnits.options.filter((i) => i.id == hgType.flowUnits.value)
  let ret = p.length ? p[0].value : ''
  return ret
}

/**
 * Returns the NWSCMS gauge configured
 * "Prefered Flow Units" setting for official hydrograph.
 * @param {*} gaugeConfig
 * @returns string
 */
export function getFlowUnits(gaugeConfig, hgType) {
  if (!gaugeConfig || gaugeConfig === null) return ''
  try {
    let v = gaugeConfig[hgType].flowUnits.options.filter((i) => i.id == gaugeConfig[hgType].flowUnits.value)
    return v ? v[0].value : ''
  } catch (err) {
    console.error(`getFlowUnits failed with :${hgType}: `, gaugeConfig)
    return ''
  }
}

/**
 * Converts the 'kcfs' string to 'cfs' else returns value such as 'ft'
 * @param string value
 * @returns string
 */
export function kcfsToCfsString(value, hg = false) {
  if (!hg) {
    return (value && value === 'kcfs') || value === 'cfs' ? 'cfs' : value
  }
  return getFlowUnitsByConfig(hg)
}

/**
 * Checks for false values
 * and if found returns true else false.
 * @param string value
 * @returns true | false
 */
export function isFalsy(value) {
  if (isNaN(value)) return true
  if (!isFinite(value)) return true
  if (value <= -999) return true
  if (value == '') return true
  if (value == undefined) return true
  return false
}

/**
 * Helper function (this file)
 * Get the stage and flow units config from the gauge API
 */
export function getGaugeUnits(gauge) {
  let stage = (gauge.flood || '').stageUnits
  let flow = (gauge.flood || '').flowUnits
  return {
    stage: stage,
    flow: flow,
  }
}

/**
 * Fastest way to find min, max in an array of integers per this article;
 * https://codeburst.io/javascript-finding-minimum-and-maximum-values-in-an-array-of-objects-329c5c7e22a2
 * @param {*} arr
 */
export function findMinMax(arr) {
  if (!arr.length) return [null, null]
  let min = arr[0],
    max = arr[0]
  for (let i = 1, len = arr.length; i < len; i++) {
    let v = arr[i]
    min = v < min ? v : min
    max = v > max ? v : max
  }
  return [min, max]
}

/**
 * Parse out flood categories data to match existing series data structure.
 * @param {*} series
 * @param {*} gaugeConfig
 * @returns object
 */
export function parseFloodCats(series, gaugeConfig) {
  if (!series.dataExists || !gaugeConfig) return null
  let data = { stage: {}, flow: {}, stage_min: null, stage_max: null, flow_min: null, flow_max: null }
  for (let [k, v] of Object.entries(gaugeConfig.flood.categories)) {
    if (v.stage && v.stage.value > -999) {
      data.stage[k] = v.stage.value
    }
    if (v.flow && v.flow.value > -999) {
      data.flow[k] = v.flow.value
    }
  }
  // DRY on stage and flow values.
  let stageVals = Object.values(data.stage)
  let flowVals = Object.values(data.flow)
  if (stageVals.length) {
    // checked
    let _minMax = findMinMax(stageVals)
    data.stage_min = _minMax[0]
    data.stage_max = _minMax[1]
  }
  if (flowVals.length) {
    let _minMax = findMinMax(flowVals)
    data.stage_min = _minMax[0]
    data.stage_max = _minMax[1]
  }
  return {
    data: data,
    stageUnits: series.y0.units,
    flowUnits: series.y1.units,
    hasData: stageVals.length > 0 && flowVals.length > 0,
  }
}

/**
 * Fetch with AbortController and timeout
 * https://dmitripavlutin.com/timeout-fetch-request/
 * @param url resource
 * @param object options
 * @returns response
 */
export async function fetchWithTimeout(resource, options) {
  // const { timeout = 8000 } = options
  const { timeout = 120000 } = options // Use longer (2 minute) timeout to allow UAT plenty of time to fetch from API back-end.

  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(), timeout)

  const response = await fetch(resource, {
    ...options,
    signal: controller.signal,
  })
  clearTimeout(id)
  return response
}

/**
 * @param object series
 * @param string streamflow
 * @returns object
 */
export function getStreamflowConfig(series, streamflow) {
  let missing = { name: `${streamflow} (Missing)`, enabled: false, visible: false }
  if (
    !series ||
    !series.nwm ||
    !series.nwm[streamflow] ||
    !series.nwm[streamflow].data ||
    series.nwm[streamflow].data.length === 0
  )
    return missing
  if (isMissing(series.nwm[streamflow]['ymax']) || isMissing(series.nwm[streamflow]['ymin'])) return missing

  // @TODO: NWSCMS configuration here
  let chartColor = streamflow in seriesStyle ? seriesStyle[streamflow].color : '#ffffff'
  return {
    name: seriesStyle[streamflow].name,
    id: streamflow,
    yaxis: seriesStyle[streamflow].axis,
    data: series.nwm[streamflow].data,
    labels: series.nwm[streamflow].labels,
    color: chartColor,
    lineWidth: seriesStyle[streamflow].lineWidth,
    marker: seriesStyle[streamflow].marker,
    unitString: seriesStyle[streamflow].unitString,
    ymin: series.nwm[streamflow].ymin,
    ymax: series.nwm[streamflow].ymax,
    xmin: series.nwm[streamflow].xmin,
    xmax: series.nwm[streamflow].xmax,
    validTime: series.nwm[streamflow].validTime || 'N/A',
  }
}

/**
 *
 * @param object gauge
 * @param string stageOrFlow
 * @returns sorted array
 */
export function getLowWaterStageRecords(gauge, stageOrFlow) {
  let arr = (gauge.flood || false) && (gauge.flood.lowWaters || false).historic
  if (!arr && !Array.isArray(arr)) return false
  let arrSorted = arr.sort((a, b) => {
    // Highest stage or flow at top, lowest at bottom of sorted array
    return a[stageOrFlow] - b[stageOrFlow]
  })
  return arrSorted
}

/**
 *
 * @param object series
 * @param object gauge
 * @returns object
 */
export function getLowWaterStageLine(series, gaugeConfig, primaryUnits) {
  if (!gaugeConfig || !gaugeConfig.flood || !gaugeConfig.flood.lowThreshold) return null

  // This is a good example of easily grabbing value from the JSON gaugeConfig selections.
  let unitVal = gaugeConfig.flood.lowThreshold.units.value
  let unitOpts = gaugeConfig.flood.lowThreshold.units.options.find((i) => i.id === unitVal)

  let unit = unitOpts ? unitOpts.value : ''
  let val = gaugeConfig.flood.lowThreshold.value
  if (val == null) return null
  let valString = gaugeConfig.flood.lowThreshold.value
  if (checkRatings(series.ratings) && unit != primaryUnits && primaryUnits == 'ft') {
    switch (unit) {
      case 'cfs':
        val = ratingsLookup(series.ratings, val, 'flow', unit)
        break
      case 'kcfs':
        val = ratingsLookup(series.ratings, val, 'flow', unit)
        break
      default:
        // feet
        unit = 'ft'
        break
    }
  }
  let config = {
    id: 'line_lowwater',
    name: 'Low Water',
    color: '#906320',
    width: 2,
    zIndex: 5,
    value: val,
    dashStyle: 'dot',
    unit: unit,
    yAxis: unit === 'ft' && series.y0.units === 'ft' ? 0 : 1,
    label: {
      text: `Low Threshold: ${valString} ${unit}`,
      align: 'right',
      x: -5,
      style: {
        zIndex: 5,
        color: 'black',
        fontWeight: 'bold',
        fontStyle: 'italic',
      },
    },
  }
  return config
}

/**
 * The white box in upper left corner of the chart showing last observed value
 * @param object chart
 * @param object series
 * @returns null
 */
export function latestObservedBox(chart, series) {
  if (!chart || !series || !series.latest_observed || !series.latest_observed.msg) return

  chart.latestObservedBox = chart.renderer
    .label(series.latest_observed.msg, chart.plotLeft + 10, chart.plotTop + 10)
    .css({
      color: '#4040FF',
    })
    .attr({
      fill: '#fff',
      padding: 4,
      r: 5,
      zIndex: 7,
      //stroke: 'black', // No border for the info box.
      margin: 5,
      opacity: 0.85,
      'stroke-width': 1,
      'font-size': 10,
      'font-weight': 'bold',
      'font-family': 'arial',
      id: 'test_' + series.version.chart,
    })
    .add()
}

/**
 * This is the title at top-center of the chart
 * which will show either the reach name or gauge name.
 * @param object series
 * @returns object
 */
export function getTitleText(series) {
  if (!series || series.dataExists === false) return ''
  if (series.gauge) {
    return series.gauge.name
  } else if (series.reach && series.reach.name) {
    return series.reach.name
  }
}

/**
 * For the series.dataExists configuration.
 */
export function verifyDataExists(gauge, stageflow) {
  if (!gauge || !stageflow) {
    return false
  } else {
    return true
  }
}

/**
 * Sets configuration setting for plotbands on the chart.
 * @param {*} plotbands
 * @returns object
 */
export function getPlotLinesFlowBandsConfig(series) {
  if (series.plotbands.length) {
    return {
      name: 'Flood Categories',
      id: 'plotlinebands',
      color: '#000000',
      yAxis: series.version.chart === 'nwm' ? 1 : 0,
    }
  }
  return null
}

/**
 * Adds the lowwater shading for the lowwater dotted line on the hydrographNWM (OWP-824)
 * @param {*} from
 * @param {*} to
 * @returns object
 */
export function addLowwaterShading(series, from, lowwaterStageline, flowUnits = null) {
  if (from == null && lowwaterStageline.value == null) return
  let to = lowwaterStageline.value
  if (from === to) return
  return {
    id: 'lowwaterShading',
    class: 'lowwater-shading',
    from: from,
    to: to,
    color: '#D7BF8F', // Per existing hydrograph color
    opacity: 0.1,
    yAxis: series.version.chart === 'nwm' ? 1 : 0,
  }
}

/**
 * The record stageline toggle config for the legend
 * @param {*} series
 * @returns
 */
export function getRecordStageLineConfig(series) {
  if (!series || !series.record_stageline) return []
  let val = series.record_stageline.value
  let text = series.record_stageline.label.text
  text = `Record: ${series.record_stageline.value} ${series.record_stageline.unit}`

  // We still want to display the record level in the legend but not have it scale the hydrographs. TM
  if (series.record_stageline && !isFalsy(val)) {
    return {
      name: text,
      id: series.record_stageline.id,
      color: seriesStyle['record_stageline'].color,
      marker: seriesStyle['record_stageline'].marker,
      value: series.record_stageline.value,
      yAxis: 0,
      zIndex: 5,
      enabled: true,
    }
  }
  return null
}

/**
 * Converts primary and secondary to CFS if KCFS and not missing (-999, -9999).
 * @param JSOn stageflow
 * @param string obsOrFcst
 * @param datetime now
 * @returns array of objects
 */
export function kcfsToCfsVals(stageflow, obsOrFcst, now) {
  if (!stageflow[obsOrFcst]) return
  return stageflow[obsOrFcst].data.map((i) => {
    return {
      validTime: i.validTime,
      generatedTime: i.generatedTime,
      primary: stageflow[obsOrFcst].primaryUnits === 'kcfs' && !isMissing(i.primary) ? kcfsToCfs(i.primary) : i.primary,
      secondary:
        stageflow[obsOrFcst].secondaryUnits === 'kcfs' && !isMissing(i.secondary)
          ? kcfsToCfs(i.secondary)
          : i.secondary,
    }
  })
}

/**
 * Sets up the y axis extremes.
 * @param {*} series
 * @returns object
 */
export function getYAxisExtremes(series) {
  let min = Math.min(...series.ticks)
  let max = Math.max(...series.ticks)
  let ret = { min: min, max: max }
  ret['default'] = { min: min, max: max }
  return ret
}

// Transform y0, y1 Data from stageflow.observed, stageflow.forecast
// Calculates the average delta between all the tickPositions
// https://stackoverflow.com/a/40247111
export const averageDelta = ([x, ...xs]) => {
  if (x === undefined) return NaN
  else return xs.reduce(([acc, last], x) => [acc + (x - last), x], [0, x])[0] / xs.length
}

// Adjusts steps depending on difference of max and min for more-evenly spaced whole numbers
function getWholeStep(value) {
  let tickLengthOptions = [7, 8, 9, 10, 11, 12, 13]
  let step
  let mudMin = 9
  let tempStep

  //Get the steps with the lowest remainder
  for (let i = 0; i < tickLengthOptions.length; i++) {
    step = Math.round(value / tickLengthOptions[i])
    let remainder = value % tickLengthOptions[i]
    if (remainder == 0) {
      return step
    }
    if (remainder < mudMin) {
      tempStep = step
      mudMin = remainder
    }
  }
  return tempStep ? tempStep : value / 10
}

/**
 *
 * @param {*} tickArr
 * @returns
 */
export function calculateTicks(tickArr, wholes, prec) {
  //Get the range of data and create 10 evenly spaced ticks
  if (!tickArr) {
    return []
  }
  let step
  let ticks = []

  if (wholes) {
    // Step defined the distance between ticks based on the range of the data
    step = getWholeStep(Math.round(Math.ceil(tickArr[tickArr.length - 1]) - Math.floor(tickArr[0])))

    //Catch step of 0 so the while loop is not infinite
    if (step == 0) {
      return calculateTicks(tickArr, false, prec)
    }
    // Add ticks evenly spaced
    let i = Math.floor(tickArr[0])
    while (i < Math.ceil(tickArr[tickArr.length - 1])) {
      if (!ticks.includes(Math.round(i))) {
        ticks.push(Math.round(i))
      }
      i += step
    }
    // Add final value
    if (!ticks.includes(Math.round(tickArr[tickArr.length - 1]))) {
      ticks.push(Math.round(tickArr[tickArr.length - 1]))
    }
  } else {
    step = (tickArr[tickArr.length - 1] - tickArr[0]) / 10
    let i = tickArr[0]
    while (i < tickArr[tickArr.length - 1]) {
      if (!ticks.includes(parseFloat(i.toFixed(prec)))) {
        ticks.push(parseFloat(i.toFixed(prec)))
      }
      i += step
    }
    if (!ticks.includes(parseFloat(tickArr[tickArr.length - 1].toFixed(prec)))) {
      ticks.push(parseFloat(tickArr[tickArr.length - 1].toFixed(prec)))
    }
  }
  // If last two ticks are too close together, remove the second to last one
  if (ticks[ticks.length - 1] - ticks[ticks.length - 2] < step * 0.7) {
    ticks.splice([ticks.length - 2], 1)
  }
  return ticks.sort((a, b) => a - b)
}

/**
 * Custom function to get the extremes.
 * @param {*} data - array of arrays or array of objects
 * @param {*} pos - can be 0,1 or 'primary', 'secondary'
 * @returns
 */
export function getExtremes(data, pos = 1) {
  if (!data.length) return { min: null, max: null }
  let sortedData = data.sort((a, b) => a[pos] - b[pos])
  let min = sortedData[0][pos]
  let max = sortedData[sortedData.length - 1][pos]
  return { min: min, max: max }
}

/**
 * Convert between flow types KCFS and CFS
 * @param {*} value - integer
 * @param {*} cin - value type coming
 * @param {*} cout - value type to send back
 */
export function flowType(value, cin, cout) {
  if (cin === 'kcfs' && cout === 'cfs') {
    return kcfsToCfs(value)
  } else if (cin === 'cfs' && cout === 'kcfs') {
    return cfsToKcfs(value)
  }
  return value
}

/**
 * Return the min, max extremes of series data.
 * If start and end UNIX datetimestamps are applied then it will get the
 * min and max for that range of data.
 *
 * If seriesDataPrimary is passed the values will be returned as FT
 * else if both seriesDataPrimary and seriesDataSecondary are used
 * then values will be returned as CFS or KCFS.
 * @param {*} seriesData
 * @param {*} start
 * @param {*} end
 * @returns array [min, max]
 */
export function getMinMaxSeriesDataYaxis(seriesDataPrimary = null, seriesDataSecondary = null) {
  if (!seriesDataPrimary) return [0, 0]

  let obsMin, obsMax, fcstMin, fcstMax
  let stages = [],
    flows = []
  // This block is stage values.
  if (seriesDataPrimary && seriesDataPrimary.observed && seriesDataPrimary.observed.length) {
    let seriesOBSMapped = []
    seriesOBSMapped = seriesDataPrimary.observed.map((i) => i[1])
    obsMin = Math.min.apply(null, seriesOBSMapped)
    stages = [obsMin]
    obsMax = Math.max.apply(null, seriesOBSMapped)
    stages = [...stages, obsMax]
  }
  // This block is stage values.
  if (seriesDataPrimary && seriesDataPrimary.forecast && seriesDataPrimary.forecast.length) {
    let seriesFCSTMapped = []
    seriesFCSTMapped = seriesDataPrimary.forecast.map((i) => i[1])
    fcstMin = Math.min.apply(null, seriesFCSTMapped)
    stages = [...stages, fcstMin]
    fcstMax = Math.max.apply(null, seriesFCSTMapped)
    stages = [...stages, fcstMax]
  }

  // Secondary data
  // If seriesDataSecondary variable is passed we should know
  // that all "values" will be interpreted as CFS or KCFS and not FT.
  if (seriesDataSecondary && Object.keys(seriesDataSecondary.nwm).length) {
    for (let [k, v] of Object.entries(seriesDataSecondary.nwm)) {
      let max = Math.max.apply(
        null,
        v.map((i) => i[1])
      )
      let min = Math.min.apply(
        null,
        v.map((i) => i[1])
      )
      flows = [...flows, max, min]
    }
  }
  let values = [...stages, ...flows]
  let vals = [Math.min(...values), Math.max(...values)].filter((i) => i != 0 && !isFalsy(i))
  if (vals) return vals
  return []
}

/**
 * Iterates through the yAxis' and prevents the labels for
 * overlapping by hiding them or showing them if no overlap. TM
 * @param {*} chart
 */
export function preventYaxisTickOverlap(chart) {
  if (!chart || (chart && !chart.yAxis)) return
  chart.yAxis.forEach((yAxis) => {
    // Have to pull the array as it maintains the ordering of the labels.
    var tickMarks = yAxis.tickPositions
    // The ticks are an object so no ordered so you need to used
    // the array above to make sure to get the order correct.
    var ticks = yAxis.ticks
    var prevTop

    if (tickMarks && tickMarks.length) {
      tickMarks.forEach((v) => {
        if (ticks[v] && ticks[v].label) {
          var lxy = ticks[v].label.xy
          if (lxy) {
            var bbox = ticks[v].label.getBBox()
            var top = lxy.y - bbox.height
            if (prevTop && lxy.y > prevTop) {
              ticks[v].label.hide()
              ticks[v].gridLine.hide()
            } else {
              ticks[v].label.show()
              ticks[v].gridLine.show()
              prevTop = top
            }
          }
        }
      })
    }
  })
}

/**
 * Adjust the series ticks by the total extreme values.
 * @param {*} seriesTicks
 * @param {*} totalExts
 * @returns array of objects
 */
export function adjustYAxisTicks(chartType, seriesTicks, rawData, totalExts, series) {
  if (
    !seriesTicks.length ||
    (series && series.hasOwnProperty('obsDataSecAllUnknown') && series.obsDataSecAllUnknown === true)
  ) {
    // In case we don't have any seriesTicks like with BIGI1 and LCJU1
    // due to the switch on primaryUnits.
    let primOrSec = 'secondary'
    if (chartType === 'official') primOrSec = 'primary'
    return [...new Set(rawData.map((i) => i[primOrSec]))]
  }
  let tempTicks = [...seriesTicks]
  for (let i = tempTicks.length - 1; i > 0; i--) {
    // Don't compute if totalExts item is a float.
    if (seriesTicks[i] > totalExts[1]) {
      seriesTicks.splice(i, 1)
    }
  }
  for (let i = 0; i < tempTicks.length - 1; i++) {
    // Don't compute if totalExts item is a float.
    if (seriesTicks[i] < totalExts[0]) {
      seriesTicks.splice(i, 1)
    }
  }
  return [...new Set(seriesTicks.sort((a, b) => a - b))]
}

/**
 * Gets the precision of a number.
 * https://stackoverflow.com/questions/9553354/how-do-i-get-the-decimal-places-of-a-floating-point-number-in-javascript
 * @param {*} a
 * @returns int
 */
export function getPrecision(a) {
  if (!isFinite(a) || isNaN(a)) return 0
  var e = 1,
    p = 0
  while (Math.round(a * e) / e !== a) {
    e *= 10
    p++
  }
  // For cases like gauge SHJC1; cannot return 0 (MS)
  if (p == 0) return 1
  return p
}

/**
 * Convert between CFS and KCFS based on
 * stageflow[observed|forecast].[primaryUnits|secondaryUnits]
 * and the series.flowUnitsByCMS.
 * @param {*} val
 * @param {*} dataUnits
 * @param {*} cmsUnits
 * @returns int
 */
export function convertFlowValue(val, dataUnits, cmsUnits) {
  switch (dataUnits) {
    case 'kcfs':
      if (cmsUnits === 'cfs') {
        val = kcfsToCfs(val)
      }
      break
    case 'cfs':
      if (cmsUnits === 'kcfs') {
        val = cfsToKcfs(val)
      }
      break
  }
  return val
}

/**
 * Remove duplicate objects with secondary properties
 * that have duplicate values from array of objects
 * like the series.rawData with elements being like;
 * [ { primary: 1, secondary: 5.6, type: 'footer' }, { primary: 2, secondary: 5.6, type: 'footer'} ]
 * @param {*} arr
 * @returns array of objects
 */
export function removeSecondaryDuplicates(arr) {
  let r = []
  let e = []
  let t = [...arr]
  for (let i = 0; i < arr.length - 1; i++) {
    let v = t[i].secondary
    if (!e.includes(v)) {
      e = [...e, v]
      r = [...r, t[i]]
    }
  }
  return r
}

/**
 * Return a string value of float that was
 * not rounded at the fixed position.
 * Example: toFixedPos(2.567, 1)
 * result: 2.5
 * @param {*} num
 * @param {*} pos
 * @returns string
 */
export function toFixedPos(num, pos = 0) {
  if (/\./.test(num) === false) return `${num}.0`
  var re = new RegExp('^-?\\d+(?:.\\d{0,' + (pos || -1) + '})?')
  let v = num.toString().match(re)[0]
  return v
}

/**
 * Set up the normal pool stageline on the hydrograph.
 * @param {*} gaugeConfig
 * @param {*} gauge
 * @returns object
 */
export function getNormalPoolFloodThreshHold(gaugeConfig, gauge) {
  if (!gaugeConfig || !gaugeConfig.flood || !gaugeConfig.flood.normalThreshold) return null
  let val = gaugeConfig.flood.normalThreshold.value
  // Figure out the flow type.
  let unitsValue = gaugeConfig.flood.normalThreshold.units.value
  let unitType = gaugeConfig.flood.normalThreshold.units.options.find((i) => i.id === unitsValue)
  let unitSymbol = ''
  if (unitType && unitType.value === 'ft') {
    unitSymbol = `'`
  }
  if (!isFalsy(val)) {
    return {
      name: seriesStyle['normal_pool'].name,
      color: seriesStyle['normal_pool'].color,
      width: 2,
      zIndex: 5,
      value: Number(val),
      unit: unitType.value,
      dashStyle: 'line',
      id: 'normal_pool',
      enabled: true,
      yAxis: 0,
      label: {
        text: `Normal Pool: ${val}${unitSymbol}`,
        align: 'right',
        x: -5,
        enabled: true,
        style: {
          zIndex: 6,
          color: '#000',
          fontWeight: 'bold',
          fontStyle: 'italic',
        },
      },
    }
  }
  return null
}

/**
 * Get the normal pool legend button configuration.
 * @param {*} np
 * @returns object
 */
export function getNormalPoolFloodThreshHoldConfig(np) {
  if (!np) return null
  let text = np.label.text
  if (np.unit === 'ft') {
    text = `Normal Pool: ${np.value} ft`
  }
  if (!isFalsy(np.value)) {
    return {
      name: text,
      id: np.id,
      color: seriesStyle['normal_pool'].color,
      marker: seriesStyle['normal_pool'].marker,
      value: np.value,
      yAxis: 0,
      zIndex: 5,
      enabled: true,
    }
  }
  return null
}

/**
 * Converts between CFS and KCFS. Used by getRatingForStage function.
 * @param {*} value
 * @param {*} from
 * @param {*} to
 * @param {*} fixed
 * @returns float
 */
export function convertStageflow(value, from, to, fixed = 1) {
  if (from === 'kcfs' && to === 'cfs') {
    return Number(parseFloat(value * 1000).toFixed(fixed))
  } else if (from === 'cfs' && to === 'kcfs') {
    return Number(parseFloat(value / 1000).toFixed(fixed))
  }
  return value
}

/**
 * Taken from C++ code. Gets the ratings value based
 * on given stage value.
 * The value is converted to either CFS or KCFS based
 * on the 'from' and 'to' configuration.
 * @param {*} ratings
 * @param {*} stage
 * @param {*} useComputedRating
 * @param {*} ratingUnits
 * @returns float
 */
export function getRatingForStage(ratings, stage, from, to, prec = 1) {
  let rb = Math.min.apply(
    null,
    ratings.map((i) => i.stage)
  )
  let rf = Math.max.apply(
    null,
    ratings.map((i) => i.stage)
  )

  // check size, in range
  if (stage < rb.stage) {
    return false
  }

  if (stage > rf.stage) {
    return false
  }

  let stageRange = { min: null, max: null }
  let flowRange = { min: null, max: null }

  for (let x = 0; x <= ratings.length - 1; x++) {
    let currStage = ratings[x].stage
    let currFlow = ratings[x].flow

    if (currStage === stage) {
      return convertStageflow(currFlow, from, to, prec)
    } else {
      if (currStage === stage) {
        return currFlow
      } else if (currStage < stage) {
        stageRange.min = currStage
        flowRange.min = currFlow

        if (currStage > stageRange.max) {
          stageRange.max = currStage
          flowRange.max = currFlow
        }
      } else {
        stageRange.max = currStage
        flowRange.max = currFlow

        if (currStage <= stageRange.min) {
          stageRange.min = currStage
          flowRange.min = currFlow
        }
        break
      }
    }
  }
  let r = (stage - stageRange.min) / (stageRange.max - stageRange.min)
  let f = flowRange.min + r * (flowRange.max - flowRange.min)

  return convertStageflow(f, from, to, prec)
}

/**
 * Taken from AHPS C++. Compute Stage value for given
 * ratings value.
 * @param {*} ratings
 * @param {*} rating
 * @returns
 */
export function getStageForRating(ratings, rating) {
  let rb = Math.min.apply(
    null,
    ratings.map((i) => i.flow)
  )
  let rf = Math.max.apply(
    null,
    ratings.map((i) => i.flow)
  )

  // check size, in range
  // if outside the ratings range then return false.
  if (rating < rb.flow || rating > rf.flow) {
    return false
  }

  let stageRange = { min: null, max: null }
  let flowRange = { min: null, max: null }

  for (let x = 0; x <= ratings.length - 1; x++) {
    let currStage = ratings[x].stage
    let currFlow = ratings[x].flow

    if (currFlow === rating) {
      return currStage
    } else {
      if (currFlow === rating) {
        return currStage
      } else if (currFlow < rating) {
        flowRange.min = currFlow
        stageRange.min = currStage

        if (currFlow > flowRange.max) {
          flowRange.max = currFlow
          stageRange.max = currStage
        }
      } else {
        flowRange.max = currFlow
        stageRange.max = currStage

        if (currFlow <= flowRange.min) {
          flowRange.min = currFlow
          stageRange.min = currStage
        }
        break
      }
    }
  }

  let r = (rating - flowRange.min) / (flowRange.max - flowRange.min)
  let s = (stageRange.min + r) * (stageRange.max - stageRange.min) * 10
  return s
}

/**
 * Add additional values to series ticks array.
 * @param {*} arr - series.ticks
 * @param {*} rd  - series.rawData
 * @param {*} sp  - series.precision
 * @returns array
 */
export function adjustTickGaps(arr, rd, sp) {
  if (!arr || !arr.length) return []
  let newArr = []
  for (let k = 0; k <= arr.length - 1; k++) {
    let next = arr[k + 1]
    let curr = arr[k]
    newArr = [...newArr, curr]
    if (next - curr > 5) {
      for (let i = curr + 1; i < next; i++) {
        if (!isFalsy(i)) {
          newArr = [...newArr, i]
        }
      }
    }
  }
  let retArr = []
  // Set proper decimal precision
  for (let i = 0; i < newArr.length; i++) {
    retArr = [...retArr, parseFloat(newArr[i].toFixed(sp))]
  }
  // Make sure our series.ticks array is within
  // the range of our series.rawData for display purposes.
  if (retArr.length && rd.length && retArr[0] > rd[0].primary) {
    retArr = [Math.floor(rd[0].primary), ...retArr]
  }
  if (retArr.length && rd.length && retArr[retArr.length - 1] < rd[rd.length - 1].primary) {
    retArr = [...retArr, Math.ceil(rd[rd.length - 1])]
  }
  // We want all whole numbers!
  retArr = [...new Set(retArr.map((i) => Math.floor(i)))]
  let finalArr = [],
    curr = null,
    last = null
  for (let i = 0; i < retArr.length; i++) {
    last = curr
    curr = retArr[i]
    if (last != null && curr - last > 1) {
      for (let j = last; j <= curr; j++) {
        finalArr = [...finalArr, j]
      }
    }
  }
  finalArr = [...finalArr, ...retArr]
  finalArr = [...new Set(finalArr)].sort((a, b) => a - b)
  return finalArr
}

/**
 * Add additional values to the series raw data.
 * @param {*} ticks
 * @param {*} rawData
 * @param {*} ratings
 * @param {*} units
 * @param {*} precision
 * @returns array of objects
 */
export function adjustRawDataGaps(chartType, series, gaugeConfig) {
  if (!series.ticks || !series.ticks.length) return series.rawData
  if (!series.rawData || !series.rawData.length || !series.ratings) return []
  // Make sure we have whole values from the rawData primaries included.
  let ticks = [...new Set(series.rawData.map((i) => Math.trunc(i.primary))), ...series.ticks]
  // We don't want to include existing primary values.
  // This causes an issue with display on highcharts.
  let prims = series.rawData.map((i) => i.primary)
  let newRawData = [...series.rawData]
  let values = [...series.wholes, ...ticks]
  let ratingsStageMin = Math.min.apply(
    null,
    series.ratings.map((i) => i.stage)
  )
  let ratingsStageMax = Math.min.apply(
    null,
    series.ratings.map((i) => i.stage)
  )
  values.forEach((i) => {
    if (!prims.includes(i) && i >= ratingsStageMin && i <= ratingsStageMax) {
      //let sec = getRatingForStage(series.ratings, i, 'cfs', series.y1.units, series.precision)
      let sec = ratingsLookup(
        series.ratings,
        i,
        'flow',
        getFlowUnitsByConfig(gaugeConfig && gaugeConfig[chartType === 'official' ? 'hydrograph' : 'hydrographNWM'])
      )
      if (isFalsy(sec)) return false
      sec = series.flowUnitsByCMS === 'kcfs' && series.y1.units === 'kcfs' ? cfsToKcfs(sec) : sec
      newRawData = [
        ...newRawData,
        {
          primary: i,
          secondary: sec,
          type: 'adjustments0',
        },
      ]
    }
  })
  return newRawData
}

/**
 * If less than 2 wholes values
 * then "create" additional values for processing.
 * @param {*} wholes
 * @returns array of numbers
 */
export function processUnderTwoNumbers(wholes) {
  if (wholes.length > 3) return wholes
  let top = Math.max(...wholes) + 1
  let bot = Math.min(...wholes) - 1
  let ret = [bot, ...wholes, top]
  return ret
}

/**
 * Return the CMS configured value for floodCatUnits
 * @param {*} gaugeConfig
 * @returns string ft|kcfs|cfs
 */
export function getFloodCatUnits(gaugeConfig) {
  if (
    !gaugeConfig ||
    !gaugeConfig.floodCatUnits ||
    !gaugeConfig.floodCatUnits.options.length ||
    isMissing(gaugeConfig.floodCatUnits.value)
  )
    return
  let val = gaugeConfig.floodCatUnits.options.filter((i) => i.id === gaugeConfig.floodCatUnits.value)
  return val && val.length ? val[0].value : null
}

/**
 * Configures the flood categories key object with stage and converted flows
 * based on the unitsConf passed in.
 * Will skip missing stages (-999, -9999) but will include stage or flow
 * values that are missing.
 * Example: { major: { stage: 24, flow: { kcfs: -999, cfs: 1.300 } }, minor: { stage: 20.4, flow: { kcfs: 0.003, cfs: 3.000 } }  }
 * @param {*} gauge
 * @param {*} ratings
 * @param {*} precision
 * @param {*} unitsConf
 * @returns object
 */
export function getFloodCategories(gauge, ratings, precision, unitsConf) {
  if (!gauge || !gauge.flood || !gauge.flood.categories) return null
  let flood = {}
  // Precision purposefully configured differently for flood cats.
  precision = 3
  for (let [key, vals] of Object.entries(gauge.flood.categories)) {
    if (isMissing(vals.stage) && isMissing(vals.flow)) continue
    flood[key] = {}
    flood[key]['flow'] = {}
    flood[key]['flow']['kcfs'] = -999
    flood[key]['flow']['cfs'] = -999
    flood[key]['stage'] = vals.stage
    //If flow is set via CMS, use that
    if (!isMissing(vals.flow)) {
      switch (gauge.flood.flowUnits) {
        case 'cfs':
          flood[key]['flow']['cfs'] = vals.flow
          flood[key]['flow']['kcfs'] = cfsToKcfs(vals.flow, precision)
          break
        case 'kcfs':
          flood[key]['flow']['kcfs'] = vals.flow
          flood[key]['flow']['cfs'] = kcfsToCfs(vals.flow, precision)
          break
        case 'ft':
          flood[key]['flow'] = vals.flow
      }
      if (isMissing(vals.stage)) {
        let s = ratingsLookup(ratings, vals.flow, 'flow', unitsConf)
        if (s) {
          flood[key]['stage'] = s
        }
      }
      //If flow is not set and ratings table is available calculate flow levels
    } else if (ratings && !isMissing(vals.stage)) {
      let f = ratingsLookup(ratings, vals.stage, 'stage', unitsConf)
      if (f) {
        // Assumption that all ratings values are CFS
        flood[key]['flow']['kcfs'] = cfsToKcfs(f, precision)
        flood[key]['flow']['cfs'] = f
      }
    }
  }
  return flood
}

/**
 * Make sure we have more than 3 array
 * of objects with the series.ratings.
 * @param series.ratings r
 * @returns true | false
 */
export function checkRatings(r) {
  return r && r.length > 3 ? true : false
}

/**
 * Get the extremes for stageflow data.
 * @param array seriesData - series.y0.seriesData[observed|forecast]
 * @returns object
 */
export function getStageflowExtremes(seriesData, dtype) {
  if (!seriesData) return {}
  let obsMin, obsMax, fcstMin, fcstMax, min, max
  if (seriesData && seriesData[dtype] && Object.keys(seriesData[dtype]).length) {
    if (seriesData.observed && seriesData.observed.data && seriesData.observed.data.length) {
      obsMin = Math.min.apply(
        null,
        seriesData.observed.data.map((i) => i.primary)
      )
      obsMax = Math.max.apply(
        null,
        seriesData.observed.data.map((i) => i.primary)
      )
    }
    if (seriesData.forecast && seriesData.forecast.data && seriesData.forecast.data.length) {
      fcstMin = Math.min.apply(
        null,
        seriesData.forecast.data.map((i) => i.primary)
      )
      fcstMax = Math.max.apply(
        null,
        seriesData.forecast.data.map((i) => i.primary)
      )
    }
    let total = [obsMin, obsMax, fcstMin, fcstMax].filter((i) => i != 0 && !isFalsy(i))
    min = Math.min(...total)
    max = Math.max(...total)
  }
  return { min: min, max: max }
}

/**
 * Get the streamflow data extremes.
 * @param {*} seriesData - series.y1.seriesData.nwm[long_range,short_range,analysis_assim,medium_range]
 * @returns object
 */
export function getStreamflowExtremes(seriesData) {
  // If seriesDataSecondary variable is passed we should know
  // that all "values" will be interpreted as CFS or KCFS and not FT.
  let flows = [],
    min,
    max
  if (seriesData && seriesData.nwm && Object.keys(seriesData.nwm).length) {
    for (let [k, v] of Object.entries(seriesData.nwm)) {
      if (Array.isArray(v) && v.length) {
        let max = Math.max.apply(
          null,
          v.map((i) => i[1])
        )
        let min = Math.min.apply(
          null,
          v.map((i) => i[1])
        )
        flows = [...flows, max, min]
      }
    }

    if (Array.isArray(flows) && flows.length) {
      min = Math.min(...flows)
      max = Math.max(...flows)
      return { min: min, max: max }
    }
  }
  return {}
}

/**
 * Positions the "Latest " to avoid data overrun.
 * @param {*} chart
 * @returns object
 */
export function getChartPositionsByData(chart) {
  // Old version of setting the values.
  // this.latestObservedBox.attr({
  //   x: chart.plotLeft + 10,
  //   y: chart.plotTop + 10,
  // })
  // Olivia has suggested using
  // [CSS clamps](https://developer.mozilla.org/en-US/docs/Web/CSS/clamp) for mobile in the future!
  return { x: chart.plotLeft - 7, y: chart.plotTop - 77 }
}
